import React, { useEffect, useMemo, useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { CONFIG_FIELDS, CONFIG_FIELD_NAMES } from '../../config/configSchema';
import { typesService, HardwareType } from '../../services/typesService';
import { CustomField } from '../../types/configTemplate';
import { DeviceSpecificFields } from '../common/DeviceSpecificFields';
import { getTypeBadgeColor } from '../../utils/fieldTypes';

interface Props {
  hardwareType: HardwareType;
  canEdit: boolean;
  /** Called after a successful save so the parent reloads the type. */
  onSaved: () => void;
}

type Config = Record<string, unknown>;

/** The product's base configuration never carries a per-device secret. */
const PRODUCT_FIELDS = CONFIG_FIELDS.filter((f) => f.field_name !== 'password');

const asText = (value: unknown): string => (value === null || value === undefined ? '' : String(value));

/** Split a stored config into the three parts the editor shows. */
const splitConfig = (config: Config) => {
  const fixed: Record<string, string> = {};
  const extras: Config = {};
  let customFields: CustomField[] = [];
  for (const [key, value] of Object.entries(config)) {
    if (key === 'custom_fields') {
      customFields = Array.isArray(value)
        ? (value as Array<Record<string, unknown>>)
            .filter((c) => c && typeof c === 'object' && 'name' in c)
            .map((c) => ({ name: String(c.name), value: asText(c.value) }))
        : [];
    } else if (CONFIG_FIELD_NAMES.includes(key)) {
      fixed[key] = asText(value);
    } else {
      extras[key] = value;
    }
  }
  return { fixed, customFields, extras };
};

/** The reverse: one config body from the three parts, typed like a template config. */
const joinConfig = (fixed: Record<string, string>, customFields: CustomField[], extras: Config): Config => {
  const config: Config = { ...extras };
  for (const def of PRODUCT_FIELDS) {
    const raw = (fixed[def.field_name] ?? '').trim();
    if (raw === '') continue;
    if (def.value_type === 'port' || def.value_type === 'number') {
      const n = Number(raw);
      config[def.field_name] = Number.isNaN(n) ? raw : n;
    } else if (def.value_type === 'boolean') {
      config[def.field_name] = raw === 'true';
    } else {
      config[def.field_name] = raw;
    }
  }
  if (customFields.length > 0) config.custom_fields = customFields;
  return config;
};

/**
 * The "Configuration" tab of a device type: the product's base
 * configuration (schema v3.1), the first layer of every device of this
 * type. Same shape as a template config: the fixed keys, custom fields,
 * and any other key, which the gateway receives in jsonAdditionalConfig.
 */
export const ProductConfigTab: React.FC<Props> = ({ hardwareType, canEdit, onSaved }) => {
  const { t } = useI18n();
  const stored = useMemo(() => splitConfig(hardwareType.config || {}), [hardwareType]);

  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fixed, setFixed] = useState<Record<string, string>>({});
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [extrasText, setExtrasText] = useState('{}');

  const reset = () => {
    setFixed(stored.fixed);
    setCustomFields(stored.customFields);
    setExtrasText(JSON.stringify(stored.extras, null, 2));
    setError(null);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reset(); setIsEditing(false); }, [stored]);

  const handleSave = async () => {
    let extras: Config;
    try {
      const parsed = JSON.parse(extrasText.trim() || '{}');
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('not an object');
      extras = parsed as Config;
    } catch {
      setError(t('deviceTypes.productConfigExtrasInvalid'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await typesService.updateHardwareType(hardwareType.id, { config: joinConfig(fixed, customFields, extras) });
      setIsEditing(false);
      onSaved();
    } catch (err: any) {
      setError(err?.message || 'Failed to save the product configuration');
    } finally {
      setSaving(false);
    }
  };

  const isEmpty = Object.keys(hardwareType.config || {}).length === 0;
  const extrasKeys = Object.keys(stored.extras);

  return (
    <div className="tab-content product-config-tab" data-testid="tab-product-config">
      <section className="cu-section">
        <header className="cu-section-head">
          <h3>{t('deviceTypes.productConfig')}</h3>
          <p>{t('deviceTypes.productConfigHelp', { code: hardwareType.code })}</p>
          {canEdit && (
            <div className="cu-section-actions">
              {isEditing ? (
                <>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => { reset(); setIsEditing(false); }} disabled={saving}>
                    {t('common.cancel')}
                  </button>
                  <button type="button" className="btn btn-primary btn-sm" data-testid="btn-product-config-save" onClick={handleSave} disabled={saving}>
                    {saving ? t('common.saving') : t('common.save')}
                  </button>
                </>
              ) : (
                <button type="button" className="btn btn-secondary btn-sm" data-testid="btn-product-config-edit" onClick={() => setIsEditing(true)}>
                  {t('common.edit')}
                </button>
              )}
            </div>
          )}
        </header>

      {error && <div className="error-message" role="alert">{error}</div>}

      {!isEditing && isEmpty ? (
        <div className="tpd-empty"><p className="product-config-empty">{t('deviceTypes.productConfigEmpty')}</p></div>
      ) : (
        <div className="config-sources-table-wrapper">
          <table className="config-sources-table product-config-table tpd-table">
            <thead>
              <tr>
                <th>{t('configSources.field')}</th>
                <th>{t('deviceTypes.fieldType')}</th>
                <th>{t('configSources.value')}</th>
              </tr>
            </thead>
            <tbody>
              {PRODUCT_FIELDS.filter((def) => isEditing || (fixed[def.field_name] ?? '') !== '').map((def) => (
                <tr key={def.field_name}>
                  <td className="col-field"><code>{def.field_name}</code></td>
                  <td><span className="type-badge tpd-type" data-kind={def.value_type} style={{ ['--kind-color' as any]: getTypeBadgeColor(def.value_type) }}>{def.value_type}</span></td>
                  <td className="col-value">
                    {isEditing ? (
                      def.value_type === 'boolean' ? (
                        <select className="cu-input" value={fixed[def.field_name] ?? ''} onChange={(e) => setFixed({ ...fixed, [def.field_name]: e.target.value })}>
                          <option value="">—</option>
                          <option value="true">true</option>
                          <option value="false">false</option>
                        </select>
                      ) : (
                        <input
                          type="text"
                          className="cu-input mono"
                          value={fixed[def.field_name] ?? ''}
                          placeholder={t('deviceTypes.productConfigNotSet')}
                          onChange={(e) => setFixed({ ...fixed, [def.field_name]: e.target.value })}
                        />
                      )
                    ) : (
                      /pass|secret|token/i.test(def.field_name) ? '••••••' : fixed[def.field_name]
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="tpd-custom">
        <DeviceSpecificFields fields={customFields} onChange={setCustomFields} isEditing={isEditing} />
      </div>

      {(isEditing || extrasKeys.length > 0) && (
        <div className="product-config-extras">
          <label>{t('deviceTypes.productConfigExtras')}</label>
          {isEditing ? (
            <textarea
              className="json-editor"
              aria-label={t('deviceTypes.productConfigExtras')}
              rows={6}
              value={extrasText}
              onChange={(e) => setExtrasText(e.target.value)}
              spellCheck={false}
            />
          ) : (
            <pre className="json-preview">{JSON.stringify(stored.extras, null, 2)}</pre>
          )}
        </div>
      )}
      </section>
    </div>
  );
};

export default ProductConfigTab;
